import {
  Box,
  Chip,
  Dialog,
  DialogClasses,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { ForcedTheme } from 'components/ForcedTheme/ForcedTheme';
import { useMapZoomLevel } from 'hooks/useMapZoomLevel';
import { getMapDialogCommonStyles } from 'features/mapping/mapping.utils';
import { useMapEntityDrawerState } from 'features/mapping/hooks/useMapEntityDrawerState';
import { useSearchDrawerState } from 'state/useSearchDrawerState';
import { MAP_DOM_ELEMENT_ID } from '../../../../../constants';
import { LIGHTNING_LOOKBACK_OPTIONS } from './LightningLayer.constants';
import { LightningLookback, StrikeCounts } from './LightningLayer.types';
import { LightningBoltIcon } from './LightningBoltIcon';

// How high to shift PoiDialog to make room for the lightning tools card
export const LIGHTNING_TOOLS_CARD_HEIGHT = {
  mobile: 260,
  desktop: 200,
} as const;

const useStyles = makeStyles<{
  isDrawerEntitySelected: boolean;
  isSearchDrawerOpen: boolean;
}>()((theme, { isDrawerEntitySelected, isSearchDrawerOpen }) => {
  const baseStyles = getMapDialogCommonStyles(theme, {
    isDrawerEntitySelected,
    isSearchDrawerOpen,
    minWidth: 280,
    minSnapPct: 0,
    isDarkShell: true,
  });

  return {
    ...baseStyles,
    container: {
      ...baseStyles.container,
      [theme.breakpoints.down('tablet')]: {
        width: '100% !important',
        justifyContent: 'center',
        alignItems: 'flex-end',
      },
    },
    paper: {
      ...baseStyles.paper,
      [theme.breakpoints.up('tablet')]: {
        bottom: theme.spacing(2),
      },
      // On desktop, set fixed width
      [theme.breakpoints.up('laptop')]: {
        width: '590px !important',
        maxWidth: '590px !important',
      },
      // On tablet, reduce width to fit better
      [theme.breakpoints.between('tablet', 'laptop')]: {
        width: '450px !important',
        maxWidth: '450px !important',
        minWidth: '450px !important',
      },
      // Full width on mobile, with side gutters
      [theme.breakpoints.down('tablet')]: {
        left: 0,
        right: 0,
        width: 'calc(100% - 32px) !important',
        minWidth: 'unset !important',
        margin: '0 auto !important',
      },
    },
  };
});

type StatCellProps = {
  label: string;
  value: number | null;
  icon?: JSX.Element;
  isLoading: boolean;
};

const StatCell = ({
  label,
  value,
  icon,
  isLoading,
}: StatCellProps): JSX.Element => {
  const isUnavailable = value === null;
  const formattedValue = !value ? '-' : value.toLocaleString();
  return (
    <Stack
      sx={{
        flex: 1,
        textAlign: 'center',
        alignItems: 'center',
        opacity: isUnavailable ? 0.4 : 1,
      }}
    >
      {isLoading ? (
        <Skeleton variant="text" width={40} sx={{ mx: 'auto' }} />
      ) : (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <Typography variant="body1">{formattedValue}</Typography>
          {icon}
        </Stack>
      )}
      <Typography
        variant="caption"
        sx={{ textTransform: 'uppercase', opacity: 0.7 }}
      >
        {label}
      </Typography>
    </Stack>
  );
};

type LightningToolsCardProps = {
  open: boolean;
  counts: StrikeCounts | null;
  countsLoading: boolean;
  lookback: LightningLookback;
  onChangeLookback: (option: LightningLookback) => void;
  onClose?: () => void;
};

export const LightningToolsCard = ({
  open,
  counts,
  countsLoading,
  lookback,
  onChangeLookback,
  onClose,
}: LightningToolsCardProps): JSX.Element | null => {
  const { t } = useTranslation();

  const zoom = useMapZoomLevel();
  const zoomInt = zoom !== undefined ? Math.floor(zoom) : undefined;

  const isEnabled = (o: LightningLookback): boolean =>
    zoomInt === undefined || zoomInt >= o.minZoom;

  useEffect(() => {
    if (zoomInt === undefined) return;
    if (zoomInt < lookback.minZoom) {
      const available = LIGHTNING_LOOKBACK_OPTIONS.filter(
        (o) => zoomInt >= o.minZoom,
      );
      const next = available.at(-1);
      if (next && next !== lookback) onChangeLookback(next);
    }
  }, [zoomInt, lookback, onChangeLookback]);

  const { open: isDrawerEntitySelected } = useMapEntityDrawerState();
  const { open: isSearchDrawerOpen } = useSearchDrawerState();

  const { classes } = useStyles({ isDrawerEntitySelected, isSearchDrawerOpen });

  const dialogClasses = useMemo(
    (): Partial<DialogClasses> => ({
      root: classes.root,
      container: classes.container,
      paper: classes.paper,
    }),
    [classes.root, classes.container, classes.paper],
  );

  return (
    <Dialog
      open={open}
      classes={dialogClasses}
      hideBackdrop
      disableAutoFocus
      container={(): HTMLElement | null =>
        document.getElementById(MAP_DOM_ELEMENT_ID)
      }
      slotProps={{
        paper: {
          onMouseDown: (e: React.MouseEvent): void => e.stopPropagation(),
          onTouchStart: (e: React.TouchEvent): void => e.stopPropagation(),
        },
      }}
    >
      <ForcedTheme mode="dark">
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            pb: 0,
            gap: 0.5,
          }}
        >
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ flex: { xs: 1, tablet: 'unset' } }}
          >
            <LightningBoltIcon />
            <Typography variant="body2" fontWeight={600}>
              {t('map.layers.lightning.title')}
            </Typography>
          </Stack>

          {onClose && (
            <IconButton
              onClick={onClose}
              size="small"
              aria-label={t('common.close')}
              sx={{
                ml: { xs: 0, tablet: 'auto' },
                backgroundColor: 'black',
                color: 'inherit',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ px: 2, pb: 2 }}>
          <Stack>
            <Stack
              direction="row"
              divider={
                <Box
                  sx={{
                    alignSelf: 'stretch',
                    borderRight: 1,
                    borderColor: 'divider',
                  }}
                />
              }
              sx={{ px: 2, py: 1.5, width: '100%' }}
            >
              <StatCell
                label={t('map.layers.lightning.toolsCard.positive')}
                value={counts?.positive ?? null}
                icon={<LightningBoltIcon backgroundColor="#FBBE3F" />}
                isLoading={countsLoading}
              />
              <StatCell
                label={t('map.layers.lightning.toolsCard.negative')}
                value={counts?.negative ?? null}
                icon={<LightningBoltIcon backgroundColor="#B7B2AC" />}
                isLoading={countsLoading}
              />
              <StatCell
                label={t('map.layers.lightning.toolsCard.total')}
                value={counts?.total ?? null}
                isLoading={countsLoading}
              />
            </Stack>

            <Box
              role="group"
              aria-label="lookback period"
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                rowGap: 1,
                columnGap: 0.5,
              }}
            >
              {LIGHTNING_LOOKBACK_OPTIONS.map((o) => {
                const selected = o === lookback;
                return (
                  <Chip
                    key={o.key}
                    label={t(
                      `map.layers.lightning.toolsCard.lookback.${o.key}`,
                    )}
                    clickable
                    disabled={!isEnabled(o)}
                    color={selected ? 'primary' : 'default'}
                    variant="filled"
                    onClick={() => onChangeLookback(o)}
                    sx={(theme) => ({
                      flex: '1 1 calc(21% - 3px)',
                      borderRadius: '8px',
                      '& .MuiChip-label': {
                        ...theme.typography.button,
                        fontSize: theme.typography.pxToRem(13),
                      },
                    })}
                  />
                );
              })}
            </Box>
          </Stack>
        </DialogContent>
      </ForcedTheme>
    </Dialog>
  );
};
